import React from 'react';
import { Card, CardContent } from './ui/card';
import { DollarSign, Clock, ShieldCheck, Fuel } from 'lucide-react';
import { benefitsData } from '../mockData';

export const BenefitsSection = () => {
  return (
    <section id="benefits" className="py-24 bg-gray-50 relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 left-0 w-full h-full opacity-30">
        <div className="absolute top-20 right-0 w-96 h-96 bg-red-100 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {benefitsData.headline}
          </h2>
          <div className="w-20 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Quantifiable ROI that transforms your bottom line
          </p>
        </div>

        {/* Main Savings Highlight */}
        <div className="mb-16 text-center">
          <div className="inline-block bg-white border-2 border-red-600 rounded-2xl px-12 py-10 shadow-xl">
            <div className="text-6xl sm:text-7xl font-bold text-red-600 mb-4">
              {benefitsData.annualSavings}
            </div>
            <div className="text-xl text-gray-700 uppercase tracking-wider">
              Saved Per Truck Annually
            </div>
          </div>
        </div>

        {/* Savings Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {benefitsData.savingsBreakdown.map((item, index) => (
            <Card 
              key={index}
              className="bg-white border-gray-200 hover:border-red-600 hover:shadow-lg transition-all duration-300"
            >
              <CardContent className="p-8 text-center">
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {item.amount}
                </div>
                <div className="text-lg font-semibold text-red-600 mb-3">
                  {item.category}
                </div>
                <div className="text-gray-600">
                  {item.description}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Industry Impact */}
        <div className="bg-gradient-to-r from-white via-gray-50 to-white border border-gray-200 rounded-2xl p-8 sm:p-12 shadow-lg">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Addressing a Massive Industry Problem
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Manual coupling and poor aerodynamics cost the trucking industry billions annually. 
                Our solution tackles this inefficiency head-on with proven technology.
              </p>
            </div>
            <div className="flex-shrink-0 text-center">
              <div className="text-5xl sm:text-6xl font-bold text-red-600 mb-2">
                {benefitsData.industryImpact}
              </div>
              <div className="text-gray-600 uppercase tracking-wider">
                {benefitsData.impactDescription}
              </div>
            </div>
          </div>
        </div>

        {/* Key Benefits Grid */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <Clock className="text-red-600 mx-auto mb-3" size={32} />
            <div className="text-2xl font-bold text-gray-900 mb-1">150+</div>
            <div className="text-sm text-gray-600">Hours Saved/Year</div>
          </div>
          <div className="text-center p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <ShieldCheck className="text-red-600 mx-auto mb-3" size={32} />
            <div className="text-2xl font-bold text-gray-900 mb-1">175</div>
            <div className="text-sm text-gray-600">Lives Saved/Year</div>
          </div>
          <div className="text-center p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <Fuel className="text-red-600 mx-auto mb-3" size={32} />
            <div className="text-2xl font-bold text-gray-900 mb-1">3,000</div>
            <div className="text-sm text-gray-600">Gallons Saved/Year</div>
          </div>
          <div className="text-center p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <DollarSign className="text-red-600 mx-auto mb-3" size={32} />
            <div className="text-2xl font-bold text-gray-900 mb-1">&lt;6 Months</div>
            <div className="text-sm text-gray-600">ROI Payback</div>
          </div>
        </div>
      </div>
    </section>
  );
};
